import { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Loader2, UploadCloud, Sparkles, RefreshCcw, ArrowRight, FileText, Briefcase, Landmark, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { analyze, fetchSampleData, uploadFinancialFile, api } from "../lib/api";
import { CURRENCIES, detectDefaultCurrency } from "../lib/currencies";
import { BrandMark, BrandWordmark } from "./LandingPage";
import RestoreProDialog from "../components/RestoreProDialog";
import SiteFootnote from "../components/SiteFootnote";
import ProductTour, {
  ANALYZE_TOUR_KEY,
  ANALYZE_TOUR_STEPS,
} from "../components/ProductTour";
import WhatsNewBanner from "../components/WhatsNewBanner";
import {
  setStandardMeta,
  upsertJsonLd,
  buildOrganizationSchema,
  buildBreadcrumbSchema,
  ORG,
} from "../lib/seo";

const SECTIONS = [
  {
    title: "Income Statement",
    description: "What came in and what it cost you to earn it.",
    fields: [
      ["revenue", "Revenue", "Total sales for the period"],
      ["cogs", "Cost of Goods Sold", "Direct cost of products/services sold"],
      ["operating_expenses", "Operating Expenses (SG&A)", "Excluding depreciation/amortization"],
      ["depreciation", "Depreciation", "Non-cash expense on tangible assets"],
      ["amortization", "Amortization", "Non-cash expense on intangibles"],
      ["interest_expense", "Interest Expense", "Interest paid on debt"],
      ["tax_expense", "Tax Expense", "Income taxes for the period"],
      ["other_income", "Other Income", "Non-operating income"],
      ["other_expense", "Other Expense", "Non-operating expense"],
    ],
  },
  {
    title: "Balance Sheet — Assets",
    description: "What the business owns at period end.",
    fields: [
      ["cash", "Cash & Equivalents", ""],
      ["accounts_receivable", "Accounts Receivable", "Money customers owe"],
      ["inventory", "Inventory", ""],
      ["other_current_assets", "Other Current Assets", ""],
      ["ppe", "Property, Plant & Equipment (net)", ""],
      ["intangibles", "Intangibles & Goodwill", ""],
      ["other_non_current_assets", "Other Long-term Assets", ""],
    ],
  },
  {
    title: "Balance Sheet — Liabilities & Equity",
    description: "What it owes and what's left for owners.",
    fields: [
      ["accounts_payable", "Accounts Payable", "Money owed to suppliers"],
      ["short_term_debt", "Short-term Debt", "Including current portion of long-term debt"],
      ["other_current_liabilities", "Other Current Liabilities", ""],
      ["long_term_debt", "Long-term Debt", ""],
      ["other_long_term_liabilities", "Other Long-term Liabilities", ""],
      ["total_equity", "Total Equity", "Owners' / shareholders' equity"],
    ],
  },
  {
    title: "Cash Flow & Debt Service",
    description: "Cash actually generated and what you owe lenders this period.",
    fields: [
      ["operating_cash_flow", "Operating Cash Flow", "Cash from operating activities"],
      ["capital_expenditures", "Capital Expenditures", "Cash spent on PP&E"],
      ["scheduled_debt_principal", "Scheduled Principal Payments", "Principal due in this period"],
    ],
  },
];

const META_FIELDS = [
  ["company_name", "Business name"],
  ["period", "Period (e.g. FY 2025, Q3 2025)"],
];

function blankInput() {
  const init = {
    company_name: "",
    period: "",
    industry: "",
    currency: detectDefaultCurrency(),
    region: "",
    share_anonymously: false,
  };
  for (const sec of SECTIONS) for (const [k] of sec.fields) init[k] = "";
  return init;
}

export default function AnalyzePage() {
  const [form, setForm] = useState(blankInput());
  const [submitting, setSubmitting] = useState(false);
  const [useAi, setUseAi] = useState(true);
  const [audience, setAudience] = useState("owner");
  const [industries, setIndustries] = useState([]);
  const [regions, setRegions] = useState([]);
  const fileRef = useRef(null);

  useEffect(() => {
    api
      .get("/industries")
      .then((r) => setIndustries(r.data.industries || []))
      .catch(() => setIndustries([]));
    api
      .get("/regions")
      .then((r) => setRegions(r.data.regions || []))
      .catch(() => setRegions([]));
  }, []);

  // SEO: set title + inject HowTo + SoftwareApplication schema for /analyze.
  // HowTo can trigger Google's step-by-step SERP display; SoftwareApplication
  // makes the tool itself indexable as a discoverable app.
  useEffect(() => {
    const url = `${ORG.url}/analyze`;
    setStandardMeta({
      title: "Analyze your business — Free CFO Grade in 5 minutes | CFO Grade",
      description:
        "Paste your P&L and balance sheet numbers. Get an instant CFO-style letter grade, 24+ financial ratios, industry benchmarks, and a plain-English CFO memo. No signup.",
      url,
    });
    upsertJsonLd("analyze-graph", {
      "@context": "https://schema.org",
      "@graph": [
        buildOrganizationSchema(),
        buildBreadcrumbSchema([
          { name: "Home", url: ORG.url },
          { name: "Analyze", url },
        ]),
        {
          "@type": "HowTo",
          "@id": `${url}#howto`,
          name: "How to check your business's financial health in 5 minutes",
          description:
            "A three-step process to grade your business on 24+ financial ratios and compare against industry benchmarks — no signup, no software, free.",
          totalTime: "PT5M",
          supply: [
            { "@type": "HowToSupply", name: "Your Profit & Loss statement (last full period)" },
            { "@type": "HowToSupply", name: "Your Balance Sheet (assets, liabilities, equity)" },
            { "@type": "HowToSupply", name: "Your annual debt service (loan principal + interest)" },
          ],
          tool: [{ "@type": "HowToTool", name: "CFO Grade financial analyzer" }],
          step: [
            {
              "@type": "HowToStep",
              position: 1,
              name: "Pick your industry and region",
              text: "Select your industry (16 sectors) and region so CFO Grade loads the right benchmark set — restaurants, agencies, ecommerce, construction, etc.",
              url: `${url}#step-industry`,
            },
            {
              "@type": "HowToStep",
              position: 2,
              name: "Paste your numbers",
              text: "Enter revenue, COGS, operating expenses, current assets, current liabilities, long-term debt, and annual debt service. Upload a CSV or PDF to auto-fill the fields.",
              url: `${url}#step-numbers`,
            },
            {
              "@type": "HowToStep",
              position: 3,
              name: "Get your CFO Grade",
              text: "See your letter grade (A+ to F), EBITDA, DSCR, 24+ ratios, industry benchmark comparisons, and a plain-English memo explaining what's strong and what to fix first.",
              url: `${url}#step-result`,
            },
          ],
        },
        {
          "@type": "SoftwareApplication",
          "@id": `${url}#tool`,
          name: "CFO Grade Analyzer",
          url,
          applicationCategory: "FinanceApplication",
          operatingSystem: "Web",
          description:
            "Free web-based financial analyzer that computes 24+ ratios (EBITDA, DSCR, margins, working capital) and assigns a letter grade A+ to F based on industry benchmarks.",
          offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "USD",
          },
          publisher: { "@id": `${ORG.url}#org` },
        },
      ],
    });
    return () => {
      const el = document.getElementById("analyze-graph");
      if (el) el.remove();
    };
  }, []);
  const [uploading, setUploading] = useState(false);
  // Tracks whether the form is currently pre-populated with sample data. If
  // the user submits in this state, the resulting /report renders in sample
  // mode (all Pro sections visible, labelled "In the paid report").
  // Cleared the moment the user edits any field — that's "their data" now.
  const [sampleLoaded, setSampleLoaded] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Pre-fill industry from ?industry= query param (used by Reddit landing pages)
  useEffect(() => {
    const qsIndustry = searchParams.get("industry");
    if (qsIndustry) {
      setForm((f) => (f.industry ? f : { ...f, industry: qsIndustry }));
    }
  }, [searchParams]);

  function setField(key, val) {
    setForm((f) => ({ ...f, [key]: val }));
    // The moment a user edits anything, treat the form as their own data —
    // sample preview mode no longer applies.
    if (sampleLoaded) setSampleLoaded(false);
  }

  async function handleLoadSample() {
    try {
      const sample = await fetchSampleData();
      const next = blankInput();
      for (const k of Object.keys(sample)) next[k] = sample[k];
      setForm(next);
      // Flag that the form is currently pre-filled with sample data. We check
      // this on submit so the resulting /report renders with the same
      // "everything visible, Pro sections labelled" experience as /sample-report.
      // The flag is cleared the moment the user edits any field (see setField).
      setSampleLoaded(true);
      toast.success("Sample data loaded — Sunrise Bakery Co.");
    } catch {
      toast.error("Could not load sample data.");
    }
  }

  function handleClear() {
    setForm(blankInput());
    setSampleLoaded(false);
    toast("Form cleared.");
  }

  async function handleUpload(file) {
    if (!file) return;
    setUploading(true);
    try {
      const { parsed, rows_recognized } = await uploadFinancialFile(file);
      const next = blankInput();
      for (const k of Object.keys(parsed)) next[k] = parsed[k];
      setForm(next);
      // Uploaded their own real data — not sample preview.
      setSampleLoaded(false);
      toast.success(`Recognized ${rows_recognized} rows. Review and run analysis.`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not parse file.";
      toast.error(detail);
    } finally {
      setUploading(false);
    }
  }

  async function handleSubmit() {
    if (!form.revenue || Number(form.revenue) <= 0) {
      toast.error("Revenue is required to compute meaningful metrics.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      const META = ["company_name", "period", "industry", "currency", "region"];
      const BOOLEANS = ["share_anonymously"];
      for (const k of Object.keys(payload)) {
        if (BOOLEANS.includes(k)) {
          payload[k] = Boolean(payload[k]);
          continue;
        }
        if (payload[k] === "" || payload[k] === null) {
          if (META.includes(k)) continue;
          payload[k] = 0;
        } else if (typeof payload[k] === "string" && !META.includes(k)) {
          payload[k] = Number(String(payload[k]).replace(/,/g, "")) || 0;
        }
      }
      if (!payload.company_name) payload.company_name = "Your Business";
      if (!payload.period) payload.period = "Latest Period";
      const res = await analyze(payload, useAi, audience, sampleLoaded);
      sessionStorage.setItem("ll_last_report", JSON.stringify(res));
      if (sampleLoaded) {
        // User clicked "Load sample" and ran analysis without editing — show
        // the full Pro report with "In the paid report" labels, same as the
        // /sample-report entry point.
        sessionStorage.setItem("ll_sample_mode", "1");
      } else {
        // Real analysis on their own numbers — drop any sample-preview unlock
        // so the genuine paywall shows on Pro sections.
        sessionStorage.removeItem("ll_sample_mode");
      }
      navigate("/report");
    } catch (e) {
      console.error(e);
      toast.error("Analysis failed. Please check inputs and try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#F7F5F0] bg-grain relative">
      <WhatsNewBanner />
      <ProductTour steps={ANALYZE_TOUR_STEPS} storageKey={ANALYZE_TOUR_KEY} />
      <header className="max-w-7xl mx-auto px-6 sm:px-10 pt-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <BrandWordmark size="md" />
        </Link>
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            onClick={() => {
              localStorage.removeItem(ANALYZE_TOUR_KEY);
              window.location.reload();
            }}
            className="text-[#5C665D] hover:bg-[#D4C9B6]/40 hidden sm:inline-flex"
            data-testid="replay-tour-btn"
          >
            Replay tour
          </Button>
          <Button
            variant="ghost"
            onClick={handleClear}
            className="text-[#5C665D] hover:bg-[#D4C9B6]/40"
            data-testid="clear-form-btn"
          >
            <RefreshCcw size={14} strokeWidth={1.5} className="mr-2" />
            Clear
          </Button>
          <RestoreProDialog
            trigger={
              <Button
                variant="ghost"
                className="text-[#5C665D] hover:bg-[#D4C9B6]/40 hidden sm:inline-flex"
                data-testid="restore-pro-btn-analyze"
              >
                <KeyRound size={14} strokeWidth={1.5} className="mr-2" />
                Restore Pro
              </Button>
            }
          />
          <Button
            variant="outline"
            onClick={handleLoadSample}
            className="border-[#1F382E] text-[#1F382E] hover:bg-[#1F382E] hover:text-[#F7F5F0] transition-all duration-300"
            data-testid="load-sample-btn"
          >
            <Sparkles size={14} strokeWidth={1.5} className="mr-2" />
            Load sample
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 sm:px-10 py-10 sm:py-14">
        <div className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.22em] text-[#5C665D]">Step 1 — your numbers</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl text-[#1F382E] leading-tight mt-3 tracking-tight">
            Tell us what your books look like.
          </h1>
          <p className="text-[#5C665D] leading-relaxed mt-3">
            Use the form, or upload a CSV, Excel, or PDF file with two columns: a label and a number.
            Leave anything you don't have blank — we'll calculate what we can.
          </p>

          <div className="mt-8">
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#5C665D] mb-3">
              Reading lens
            </p>
            <div
              className="inline-flex p-1 bg-white border border-[#E5E0D8] rounded-md"
              role="tablist"
              data-testid="audience-toggle"
            >
              <button
                type="button"
                onClick={() => setAudience("owner")}
                data-testid="audience-owner-btn"
                className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm transition-all duration-200 ${
                  audience === "owner"
                    ? "bg-[#1F382E] text-[#F7F5F0]"
                    : "text-[#5C665D] hover:text-[#1F382E]"
                }`}
              >
                <Briefcase size={14} strokeWidth={1.5} />
                Owner / Operator
              </button>
              <button
                type="button"
                onClick={() => setAudience("analyst")}
                data-testid="audience-analyst-btn"
                className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm transition-all duration-200 ${
                  audience === "analyst"
                    ? "bg-[#1F382E] text-[#F7F5F0]"
                    : "text-[#5C665D] hover:text-[#1F382E]"
                }`}
              >
                <Landmark size={14} strokeWidth={1.5} />
                Analyst / Lender
              </button>
            </div>
            <p className="text-xs text-[#5C665D] mt-2 leading-relaxed max-w-xl">
              {audience === "owner"
                ? "Plain-English memo: what's working, what to fix, and a 30/60/90-day playbook for margin improvement."
                : "Credit-memo style: cash-flow adequacy, leverage, covenants, mitigants, and monitoring triggers from a lender's standpoint."}
            </p>
          </div>
        </div>

        <Tabs defaultValue="manual" className="mt-10">
          <TabsList
            className="bg-white border border-[#E5E0D8] p-1 rounded-md"
            data-testid="input-tabs"
          >
            <TabsTrigger
              value="manual"
              className="data-[state=active]:bg-[#1F382E] data-[state=active]:text-[#F7F5F0] rounded-sm px-4"
              data-testid="tab-manual"
            >
              Manual entry
            </TabsTrigger>
            <TabsTrigger
              value="upload"
              className="data-[state=active]:bg-[#1F382E] data-[state=active]:text-[#F7F5F0] rounded-sm px-4"
              data-testid="tab-upload"
            >
              CSV / Excel / PDF upload
            </TabsTrigger>
          </TabsList>

          <TabsContent value="manual" className="mt-8">
            <Card className="bg-white border-[#E5E0D8] shadow-none">
              <CardHeader>
                <CardTitle className="font-serif-display text-2xl text-[#1F382E]">
                  Business details
                </CardTitle>
                <CardDescription className="text-[#5C665D]">
                  Optional — used in the report header.
                </CardDescription>
              </CardHeader>
              <CardContent className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {META_FIELDS.map(([k, label]) => (
                  <div key={k}>
                    <Label className="text-xs uppercase tracking-[0.16em] text-[#5C665D]">{label}</Label>
                    <Input
                      value={form[k]}
                      onChange={(e) => setField(k, e.target.value)}
                      data-testid={`field-${k}`}
                      className="mt-2 bg-white border-[#E5E0D8] focus-visible:ring-[#1F382E]"
                    />
                  </div>
                ))}
                <div>
                  <Label className="text-xs uppercase tracking-[0.16em] text-[#5C665D]">
                    Industry
                  </Label>
                  <Select
                    value={form.industry || ""}
                    onValueChange={(v) => setField("industry", v)}
                  >
                    <SelectTrigger
                      className="mt-2 bg-white border-[#E5E0D8] focus:ring-[#1F382E]"
                      data-testid="field-industry"
                    >
                      <SelectValue placeholder="Choose industry…" />
                    </SelectTrigger>
                    <SelectContent>
                      {industries.map((it) => (
                        <SelectItem key={it.key} value={it.label}>
                          {it.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-[#5C665D] mt-1.5 leading-snug">
                    Sets the peer benchmark used to grade your ratios.
                  </p>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.16em] text-[#5C665D]">
                    Currency
                  </Label>
                  <Select
                    value={form.currency || "USD"}
                    onValueChange={(v) => setField("currency", v)}
                  >
                    <SelectTrigger
                      className="mt-2 bg-white border-[#E5E0D8] focus:ring-[#1F382E]"
                      data-testid="field-currency"
                    >
                      <SelectValue placeholder="Choose currency…" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {CURRENCIES.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-[#5C665D] mt-1.5 leading-snug">
                    Used for formatting and the AI memo.
                  </p>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.16em] text-[#5C665D]">
                    Region
                  </Label>
                  <Select
                    value={form.region || ""}
                    onValueChange={(v) => setField("region", v)}
                  >
                    <SelectTrigger
                      className="mt-2 bg-white border-[#E5E0D8] focus:ring-[#1F382E]"
                      data-testid="field-region"
                    >
                      <SelectValue placeholder="Auto-detect" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {regions.map((r) => (
                        <SelectItem key={r.key} value={r.key}>
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-[#5C665D] mt-1.5 leading-snug">
                    Unlocks in-region peer benchmarks.
                  </p>
                </div>
              </CardContent>
              <div className="px-6 pb-6">
                <label
                  className="flex items-start gap-3 text-sm text-[#1A1A1A] cursor-pointer p-3 rounded-md border border-dashed border-[#D4C9B6] hover:bg-[#F7F5F0]/50 transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={form.share_anonymously || false}
                    onChange={(e) => setField("share_anonymously", e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-[#1F382E]"
                    data-testid="share-anon-toggle"
                  />
                  <span className="leading-relaxed">
                    <strong className="text-[#1F382E]">Help build the peer benchmark.</strong>
                    {" "}
                    Anonymously share my <em>ratios only</em> (no business name, period, or
                    monetary amounts) so others in the same industry/region see better medians.
                  </span>
                </label>
              </div>
            </Card>

            <div className="mt-6 grid gap-6">
              {SECTIONS.map((sec) => (
                <Card key={sec.title} className="bg-white border-[#E5E0D8] shadow-none">
                  <CardHeader>
                    <CardTitle className="font-serif-display text-2xl text-[#1F382E]">
                      {sec.title}
                    </CardTitle>
                    <CardDescription className="text-[#5C665D]">
                      {sec.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {sec.fields.map(([k, label, hint]) => (
                      <div key={k}>
                        <Label className="text-xs uppercase tracking-[0.16em] text-[#5C665D]">
                          {label}
                        </Label>
                        <div className="relative mt-2">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C665D] text-[11px] uppercase tracking-wider">
                            {form.currency || "USD"}
                          </span>
                          <Input
                            type="text"
                            inputMode="decimal"
                            value={form[k]}
                            onChange={(e) => setField(k, e.target.value)}
                            placeholder="0"
                            data-testid={`field-${k}`}
                            className="bg-white border-[#E5E0D8] focus-visible:ring-[#1F382E] pl-14 tabular-nums"
                          />
                        </div>
                        {hint && (
                          <p className="text-[11px] text-[#5C665D] mt-1.5 leading-snug">{hint}</p>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="upload" className="mt-8">
            <Card className="bg-white border-[#E5E0D8] shadow-none">
              <CardHeader>
                <CardTitle className="font-serif-display text-2xl text-[#1F382E]">
                  Upload CSV, Excel, or PDF
                </CardTitle>
                <CardDescription className="text-[#5C665D]">
                  Two columns: a label (e.g. "Revenue") and a numeric value. We recognise common
                  P&L and balance-sheet line items.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div
                  className="border-2 border-dashed border-[#D4C9B6] rounded-xl p-10 text-center bg-[#F7F5F0]/40 hover:bg-[#F7F5F0] transition-colors cursor-pointer"
                  onClick={() => fileRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const f = e.dataTransfer.files?.[0];
                    if (f) handleUpload(f);
                  }}
                  data-testid="upload-dropzone"
                >
                  <UploadCloud size={28} strokeWidth={1.5} className="mx-auto text-[#1F382E]" />
                  <p className="mt-3 text-sm text-[#1F382E]">
                    {uploading ? "Parsing file…" : "Drop a .csv, .xlsx, or .pdf file here or click to browse"}
                  </p>
                  <p className="text-xs text-[#5C665D] mt-1">
                    PDFs work too — most accounting exports (QuickBooks, Xero, FreshBooks).
                    Recognised labels: Revenue, COGS, Operating Expenses, Depreciation, Interest
                    Expense, Cash, AR, Inventory, AP, Long-term Debt, Total Equity, Operating Cash
                    Flow, Capex…
                  </p>
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".csv,.xlsx,.xls,.pdf"
                    onChange={(e) => handleUpload(e.target.files?.[0])}
                    className="hidden"
                    data-testid="file-input"
                  />
                </div>

                <div className="mt-6 flex items-start gap-3 text-sm text-[#5C665D]">
                  <FileText size={16} strokeWidth={1.5} className="mt-0.5 text-[#C26D5C]" />
                  <p className="leading-relaxed">
                    Once parsed, switch back to the <strong>Manual entry</strong> tab to review and
                    adjust before running the analysis.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="mt-10 sticky bottom-4 z-20">
          <div className="bg-white border border-[#E5E0D8] rounded-xl px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 shadow-sm">
            <label className="flex items-center gap-3 text-sm text-[#1A1A1A] cursor-pointer">
              <input
                type="checkbox"
                checked={useAi}
                onChange={(e) => setUseAi(e.target.checked)}
                className="h-4 w-4 accent-[#1F382E]"
                data-testid="use-ai-toggle"
              />
              Include AI-generated CFO narrative (Claude Sonnet 4.5)
            </label>
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              data-testid="run-analysis-btn"
              className="bg-[#1F382E] hover:bg-[#142922] text-[#F7F5F0] rounded-md h-11 px-6 transition-all duration-300"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" /> Analyzing…
                </>
              ) : (
                <>
                  Run analysis <ArrowRight size={16} strokeWidth={1.5} className="ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>
      </main>
      <SiteFootnote />
    </div>
  );
}
